import React from "react";
import { Box, Flex, HStack, IconButton, Popover, PopoverBody, PopoverContent, PopoverTrigger, Spacer } from "@chakra-ui/react";
import { FilterSettings } from "../filter-settings/FilterSettings";
import SidebarToggle from "../../components/toolbar/SidebarToggle";
import CustomSwitch from "../../components/design_library/toolbar/Switch";
import PageHeading from "../../components/design_library/PageHeading";
import { RiExchangeBoxFill } from "react-icons/ri";
import { MdBook, MdShowChart } from "react-icons/md";
import { BiGroup } from "react-icons/bi";
import { FiEyeOff } from "react-icons/fi";
import { FiMoreHorizontal } from "react-icons/fi";
import { FixedDateRangeSelector } from "../../components";
import { KeyValuePair, KnowledgeBaseHelperTopics, TradeEx } from "../../types";
import { AutoSync } from "../AutoSync/AutoSync";
import KnowledgeBaseHelper from "../knowledgebase/KnowledgeBaseHelper";
import Uploads from "../uploads/Uploads";
import { useAtom, useAtomValue } from "jotai";
import { ViewModeAtom, TradesGroupingModeAtom, setTradesGroupingModeInStorage, AccountAtom } from "../../store";
import { AssignStrategyButton } from "./AssignStrategyButton";

interface TradesToolbarProps {
  showUnBooked: boolean;
  setShowUnBooked: (value: boolean) => void;
  showNoPositionGrouping: boolean;
  setShowNoPositionGrouping: (value: boolean) => void;
  showHidden: boolean;
  setShowHidden: (value: boolean) => void;
  setSelectedDateRange: (item: KeyValuePair) => void;
  trades: TradeEx[];
  onRefresh: () => void;
}

export const TradesToolbar = (props: TradesToolbarProps) => {
  const { setSelectedDateRange, trades, onRefresh } = props;
  const viewMode = useAtomValue(ViewModeAtom);
  const [groupingMode, setGroupingMode] = useAtom(TradesGroupingModeAtom);
  const accountState = useAtomValue(AccountAtom);
  
  const handleGroupingModeToggle = () => {
    const newMode = groupingMode === "underlying-date" ? "date-underlying" : "underlying-date";
    setGroupingMode(newMode);
    setTradesGroupingModeInStorage(newMode);
  };

  return (
    <Flex className="grid-toolbar-hook" mx="2" mb={2}>
      <HStack className="grid-toolbar-select" wrap={"wrap"} maxW="100vw" w="100%" spacing={2} overflowX="auto" overflowY="hidden">
        <SidebarToggle />

        <PageHeading
          title="Trades"
          description="View and manage your trades. Filter, group, and sort your trades to get the insights you need."
          icon={<RiExchangeBoxFill />}
        />

        <FilterSettings />

        <FixedDateRangeSelector
          onDateRangeSelected={setSelectedDateRange}
          storageKey="trades"
          defaultValue="all"
        />

        {viewMode === "simple" && (
          <CustomSwitch
            icon={groupingMode === "underlying-date" ? <MdShowChart size={14} /> : <MdShowChart size={14} />}
            id="grouping-mode"
            isChecked={groupingMode === "underlying-date"}
            onChange={handleGroupingModeToggle}
            mr={2}
            className="custom-switch-with-elipsis"
          >
            {groupingMode === "underlying-date" ? "Underlying → Date" : "Date → Underlying"}
          </CustomSwitch>
        )}

        {viewMode === "advanced" && (
          <CustomSwitch
            icon={<MdBook size={14} />}
            id="isChecked"
            isChecked={props.showUnBooked}
            onChange={(e: any) => {
              props.setShowUnBooked(!props.showUnBooked);
            }}
            mr={2}
            className="custom-switch-with-elipsis"
          >
            Show unbooked
          </CustomSwitch>
        )}

        {viewMode === "advanced" && (
          <CustomSwitch
            icon={<BiGroup size={14} />}
            id="isChecked"
            className="custom-switch-with-elipsis"
            isChecked={props.showNoPositionGrouping}
            onChange={(e: any) => {
              props.setShowNoPositionGrouping(!props.showNoPositionGrouping);
            }}
            mr={2}
          >
            No position group
          </CustomSwitch>
        )}

        <CustomSwitch
          icon={<FiEyeOff size={14} />}
          id="isChecked"
          className="custom-switch-with-elipsis"
          isChecked={props.showHidden}
          onChange={(e: any) => {
            props.setShowHidden(!props.showHidden);
          }}
          mr={2}
        >
          Hidden trades
        </CustomSwitch>

        {viewMode === "advanced" && accountState.selectedAccount?.id && (
          <AssignStrategyButton
            trades={trades}
            accountId={accountState.selectedAccount.id}
            onComplete={onRefresh}
          />
        )}

        <Spacer display={{ base: "none", md: "block" }} />

        {/* Desktop actions */}
        <HStack display={{ base: "none", md: "flex" }}>
          <AutoSync minimalGui={true} />
          <KnowledgeBaseHelper topic={KnowledgeBaseHelperTopics.TRANSACTIONS} />
        </HStack>

        {/* Keep Add assets visible at all sizes */}
        <Box>
          <Uploads />
        </Box>

        {/* Mobile overflow menu */}
        <Box display={{ base: "block", md: "none" }}>
          <Popover placement="bottom-end">
            <PopoverTrigger>
              <IconButton aria-label="More" icon={<FiMoreHorizontal />} size="sm" variant="ghost" />
            </PopoverTrigger>
            <PopoverContent p={2} bg="var(--bg)">
              <PopoverBody>
                <HStack spacing={3} alignItems="center">
                  <AutoSync minimalGui={true} />
                  <KnowledgeBaseHelper topic={KnowledgeBaseHelperTopics.TRANSACTIONS} />
                </HStack>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        </Box>
      </HStack>
    </Flex>
  );
};
